import { useState, useEffect, useCallback } from "react";
import { API } from "@/App";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Sun,
  Cloud,
  CloudRain,
  Snowflake,
  Wind,
  Newspaper,
  Calendar,
  ListTodo,
  Phone,
  AlertTriangle,
  Volume2,
  VolumeX,
  Loader2,
  RefreshCw,
  ExternalLink,
  Clock,
  Thermometer,
  Droplets,
  Coffee,
  MapPin,
  Navigation
} from "lucide-react";

// Weather icon mapping
const getWeatherIcon = (condition) => {
  const lower = condition?.toLowerCase() || "";
  if (lower.includes("rain") || lower.includes("shower")) return <CloudRain className="w-8 h-8 text-blue-400" />;
  if (lower.includes("snow")) return <Snowflake className="w-8 h-8 text-cyan-300" />;
  if (lower.includes("cloud") || lower.includes("overcast")) return <Cloud className="w-8 h-8 text-gray-400" />;
  if (lower.includes("wind")) return <Wind className="w-8 h-8 text-gray-300" />;
  return <Sun className="w-8 h-8 text-yellow-400" />;
};

// Format time helper
const formatTime = (hour) => {
  if (!hour && hour !== 0) return "";
  const h = Math.floor(hour);
  const m = Math.round((hour - h) * 60);
  const ampm = h >= 12 ? 'PM' : 'AM';
  const displayH = h > 12 ? h - 12 : h === 0 ? 12 : h;
  return `${displayH}:${m.toString().padStart(2, '0')} ${ampm}`;
};

export default function MorningBriefing({ open, onClose }) {
  const [briefing, setBriefing] = useState(null);
  const [loading, setLoading] = useState(false);
  const [speaking, setSpeaking] = useState(false);
  const [autoSpeak, setAutoSpeak] = useState(true);
  const [location, setLocation] = useState(() => {
    // Try to get saved location from localStorage
    return localStorage.getItem('niko_weather_location') || '';
  });
  const [gettingLocation, setGettingLocation] = useState(false);
  const [locationError, setLocationError] = useState(null);

  // Get current location using browser geolocation
  const getCurrentLocation = useCallback(() => {
    if (!navigator.geolocation) {
      setLocationError("Geolocation not supported");
      return;
    }
    
    setGettingLocation(true);
    setLocationError(null);
    
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude } = position.coords;
        // Use coordinates directly - wttr.in supports lat,lon format
        const coordLocation = `${latitude.toFixed(4)},${longitude.toFixed(4)}`;
        setLocation(coordLocation);
        localStorage.setItem('niko_weather_location', coordLocation);
        setGettingLocation(false);
      },
      (error) => {
        console.error("Geolocation error:", error);
        setLocationError("Could not get location. Please enter manually.");
        setGettingLocation(false);
        // Fallback to IP-based location (empty string tells backend to auto-detect)
        if (!location) {
          setLocation('auto');
        }
      },
      { 
        enableHighAccuracy: false, 
        timeout: 10000, 
        maximumAge: 300000 // Cache for 5 minutes
      }
    );
  }, [location]);

  // Auto-detect location on first open if not set
  useEffect(() => {
    if (open && !location) {
      getCurrentLocation();
    }
  }, [open, location, getCurrentLocation]);

  const fetchBriefing = useCallback(async () => {
    setLoading(true);
    try {
      const locationParam = location || 'auto';
      const response = await axios.get(`${API}/briefing/morning?location=${encodeURIComponent(locationParam)}`, {
        withCredentials: true
      });
      setBriefing(response.data);
      
      // Auto-speak if enabled
      if (autoSpeak && response.data?.summary_text) {
        speakText(response.data.summary_text);
      }
    } catch (error) {
      console.error("Error fetching briefing:", error);
    } finally {
      setLoading(false);
    }
  }, [location, autoSpeak]);

  useEffect(() => {
    if (open && location) {
      fetchBriefing();
    }
    return () => {
      // Stop speaking when dialog closes
      if (window.speechSynthesis) {
        window.speechSynthesis.cancel();
      }
    };
  }, [open, location, fetchBriefing]);

  const handleLocationChange = (newLocation) => {
    setLocation(newLocation);
    localStorage.setItem('niko_weather_location', newLocation);
  };

  const speakText = (text) => {
    if (!window.speechSynthesis) return;
    
    window.speechSynthesis.cancel();
    setSpeaking(true);
    
    const utterance = new SpeechSynthesisUtterance(text);
    utterance.rate = 1.0;
    utterance.pitch = 1.0;
    utterance.volume = 0.9;
    
    // Try to find a good voice
    const voices = window.speechSynthesis.getVoices();
    const preferredVoice = voices.find(v => 
      v.name.includes('Samantha') || 
      v.name.includes('Google') || 
      v.name.includes('Natural')
    );
    if (preferredVoice) {
      utterance.voice = preferredVoice;
    }
    
    utterance.onend = () => setSpeaking(false);
    utterance.onerror = () => setSpeaking(false);
    
    window.speechSynthesis.speak(utterance);
  };

  const stopSpeaking = () => {
    if (window.speechSynthesis) {
      window.speechSynthesis.cancel();
      setSpeaking(false);
    }
  };

  const toggleSpeak = () => {
    if (speaking) {
      stopSpeaking();
    } else if (briefing?.summary_text) {
      speakText(briefing.summary_text);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-hidden">
        <DialogHeader>
          <DialogTitle className="flex items-center justify-between">
            <span className="flex items-center gap-2">
              <Coffee className="w-5 h-5 text-amber-500" />
              Morning Briefing
            </span>
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={toggleSpeak}
                className={speaking ? "text-primary" : ""}
              >
                {speaking ? (
                  <VolumeX className="w-4 h-4" />
                ) : (
                  <Volume2 className="w-4 h-4" />
                )}
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={fetchBriefing}
                disabled={loading}
              >
                <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              </Button>
            </div>
          </DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
          </div>
        ) : briefing ? (
          <ScrollArea className="h-[calc(90vh-120px)] pr-4">
            <div className="space-y-6">
              {/* Greeting */}
              <div className="text-center py-4">
                <h2 className="text-2xl font-bold text-primary">{briefing.greeting}</h2>
                <p className="text-sm text-muted-foreground mt-1">
                  {new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}
                </p>
              </div>

              {/* Weather Card */}
              {briefing.weather ? (
                <Card className="bg-gradient-to-br from-blue-500/20 to-cyan-500/20 border-blue-500/30">
                  <CardContent className="pt-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        {getWeatherIcon(briefing.weather.condition)}
                        <div>
                          <p className="text-3xl font-bold">{briefing.weather.temp_f}°F</p>
                          <p className="text-sm text-muted-foreground">{briefing.weather.condition}</p>
                        </div>
                      </div>
                      <div className="text-right text-sm space-y-1">
                        <div className="flex items-center gap-2 justify-end">
                          <Thermometer className="w-4 h-4 text-orange-400" />
                          <span>Feels like {briefing.weather.feels_like_f}°F</span>
                        </div>
                        <div className="flex items-center gap-2 justify-end">
                          <Droplets className="w-4 h-4 text-blue-400" />
                          <span>{briefing.weather.humidity}% humidity</span>
                        </div>
                        <div className="flex items-center gap-2 justify-end">
                          <Wind className="w-4 h-4 text-gray-400" />
                          <span>{briefing.weather.wind_mph} mph wind</span>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center justify-between mt-2">
                      <div className="flex items-center gap-1 text-xs text-muted-foreground">
                        <MapPin className="w-3 h-3" />
                        <span>{briefing.weather.location}</span>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={getCurrentLocation}
                        disabled={gettingLocation}
                        className="h-6 text-xs"
                      >
                        {gettingLocation ? (
                          <Loader2 className="w-3 h-3 animate-spin mr-1" />
                        ) : (
                          <Navigation className="w-3 h-3 mr-1" />
                        )}
                        Update Location
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ) : (
                <Card className="bg-gradient-to-br from-blue-500/20 to-cyan-500/20 border-blue-500/30">
                  <CardContent className="pt-4">
                    <div className="flex items-center justify-between">
                      <p className="text-sm text-muted-foreground">Weather data loading...</p>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={getCurrentLocation}
                        disabled={gettingLocation}
                        className="h-8"
                      >
                        {gettingLocation ? (
                          <Loader2 className="w-4 h-4 animate-spin mr-2" />
                        ) : (
                          <Navigation className="w-4 h-4 mr-2" />
                        )}
                        Get My Location
                      </Button>
                    </div>
                    {locationError && (
                      <p className="text-xs text-red-400 mt-2">{locationError}</p>
                    )}
                  </CardContent>
                </Card>
              )}

              {/* AOC Status */}
              {briefing.aoc_status?.is_on_call && (
                <Card className="bg-amber-500/20 border-amber-500/50">
                  <CardContent className="pt-4">
                    <div className="flex items-center gap-3">
                      <Phone className="w-6 h-6 text-amber-400" />
                      <div>
                        <p className="font-semibold text-amber-300">You're On Call Today!</p>
                        <p className="text-sm text-amber-200/70">
                          {briefing.aoc_status.shifts.map(s => s.facility_name).join(", ")}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Today's Meetings */}
              <Card className="bento-card">
                <CardHeader className="pb-2">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Calendar className="w-4 h-4 text-blue-400" />
                    Today's Meetings
                    <Badge variant="outline" className="ml-auto">
                      {briefing.meetings?.length || 0}
                    </Badge>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {briefing.meetings?.length > 0 ? (
                    <div className="space-y-2">
                      {briefing.meetings.map((mtg, idx) => (
                        <div 
                          key={idx}
                          className="flex items-center justify-between p-2 rounded-lg bg-zinc-800/50"
                        >
                          <div className="flex items-center gap-3">
                            <Clock className="w-4 h-4 text-muted-foreground" />
                            <div>
                              <p className="font-medium text-sm">{mtg.title}</p>
                              {mtg.facility_name && (
                                <p className="text-xs text-muted-foreground">{mtg.facility_name}</p>
                              )}
                            </div>
                          </div>
                          <Badge variant="secondary">
                            {formatTime(mtg.start_time)}
                          </Badge>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground text-center py-2">
                      No meetings today 🎉
                    </p>
                  )}
                </CardContent>
              </Card>

              {/* Priority Tasks */}
              {briefing.tasks?.length > 0 && (
                <Card className="bento-card">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base flex items-center gap-2">
                      <ListTodo className="w-4 h-4 text-green-400" />
                      Priority Tasks
                      <Badge variant="outline" className="ml-auto">
                        {briefing.tasks.length}
                      </Badge>
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      {briefing.tasks.map((task, idx) => (
                        <div 
                          key={idx}
                          className="flex items-center gap-3 p-2 rounded-lg bg-zinc-800/50"
                        >
                          <div className="w-2 h-2 rounded-full bg-green-400" />
                          <p className="text-sm flex-1">{task.description}</p>
                          {task.category && (
                            <Badge variant="outline" className="text-xs">
                              {task.category}
                            </Badge>
                          )}
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Upcoming Deadlines */}
              {briefing.deadlines?.length > 0 && (
                <Card className="bento-card border-red-500/30">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base flex items-center gap-2">
                      <AlertTriangle className="w-4 h-4 text-red-400" />
                      Upcoming Deadlines
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      {briefing.deadlines.map((task, idx) => (
                        <div 
                          key={idx}
                          className="flex items-center justify-between p-2 rounded-lg bg-zinc-800/50"
                        >
                          <p className="text-sm flex-1">{task.description}</p>
                          <Badge 
                            variant="outline" 
                            className={
                              task.days_until <= 1 ? "text-red-400 border-red-400" :
                              task.days_until <= 3 ? "text-orange-400 border-orange-400" :
                              "text-yellow-400 border-yellow-400"
                            }
                          >
                            {task.days_until === 0 ? "TODAY" : 
                             task.days_until === 1 ? "Tomorrow" : 
                             `${task.days_until} days`}
                          </Badge>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Top News */}
              {briefing.news?.length > 0 && (
                <Card className="bento-card">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base flex items-center gap-2">
                      <Newspaper className="w-4 h-4 text-purple-400" />
                      Top Headlines
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {briefing.news.map((article, idx) => (
                        <a 
                          key={idx}
                          href={article.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="block p-2 rounded-lg bg-zinc-800/50 hover:bg-zinc-700/50 transition-colors"
                        >
                          <div className="flex items-start justify-between gap-2">
                            <div>
                              <p className="text-sm font-medium line-clamp-2">{article.title}</p>
                              <p className="text-xs text-muted-foreground mt-1">{article.source}</p>
                            </div>
                            <ExternalLink className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                          </div>
                        </a>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Motivational close */}
              <div className="text-center py-4">
                <p className="text-lg font-medium text-primary">Have a productive day! 💪</p>
              </div>
            </div>
          </ScrollArea>
        ) : (
          <div className="text-center py-8 text-muted-foreground">
            Unable to load briefing. Please try again.
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
